import { useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import { Palette as PaletteIcon } from '@mui/icons-material';
import { useThemeCustom } from '../hooks/useThemeCustom';
import usersService from '../services/usersService';
import { themeIdToType } from '../constants/ThemeType';

/**
 * Componente seletor de tema
 * Exibe um dropdown para trocar entre os temas disponíveis
 * Persiste a escolha no backend via API
 */
export default function ThemeSelector() {
  const { themeId, availableThemes, changeTheme, loading } = useThemeCustom();
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = async (event) => {
    const newThemeId = event.target.value;
    setLocalLoading(true);
    setError(null);
    
    try {
      // 1. Persistir no backend PRIMEIRO
      const themeType = themeIdToType(newThemeId);
      console.log('[ThemeSelector] Salvando tema no backend:', { themeId: newThemeId, themeType });
      await usersService.updateTheme(themeType);
      console.log('[ThemeSelector] Tema salvo no backend com sucesso');
      
      // 2. Aplicar tema localmente
      await changeTheme(newThemeId);
      
      // 3. Mostrar feedback de sucesso
      setSuccess(true);
      console.log('[ThemeSelector] Tema aplicado com sucesso');
    } catch (err) {
      console.error('[ThemeSelector] Erro ao alterar tema:', err);
      setError('Erro ao salvar tema. Tente novamente.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccess(false);
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 150,
        }}
      >
        <PaletteIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <FormControl size="small" fullWidth>
          <Select
            value={themeId}
            onChange={handleChange}
            disabled={loading || localLoading}
            sx={{
              '& .MuiSelect-select': {
                py: 0.5,
                fontSize: '0.875rem',
              },
            }}
            startAdornment={
              (loading || localLoading) && (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              )
            }
          >
            {availableThemes.map((theme) => (
              <MenuItem key={theme.id} value={theme.id}>
                <Typography variant="body2">{theme.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Snackbar de sucesso */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" variant="filled">
          Tema atualizado com sucesso!
        </Alert>
      </Snackbar>

      {/* Snackbar de erro */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
